/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { ServiceType } from 'shared';

import { closeCreateClusterOverlay, openCreateClusterOverlay } from '../store/createCluster/actions';
import { CreateClusterParams } from '../store/createCluster/types';
import { closeCreateOrganizationOverlay, openCreateOrganizationOverlay } from '../store/createOrganization/actions';
import { closeCreateProjectOverlay, openCreateProjectOverlay } from '../store/createProject/actions';
import { OpenCreateProjectOverlayParams } from '../store/createProject/types';
import { closeCreateServiceOverlay, openCreateServiceOverlay } from '../store/createService/actions';
import { closeUpdateClusterOverlay, openUpdateClusterOverlay } from '../store/updateCluster/actions';
import { EntityId } from '../types/api/EntityId';

export const useControlOverlays = () => {
  const dispatch = useDispatch();
  const { organization_id, project_id, cluster_id } = useParams<{
    project_id: string;
    organization_id: string;
    cluster_id: string;
  }>();

  const doCloseCreateProjectOverlay = useCallback(() => dispatch(closeCreateProjectOverlay()), [dispatch]);
  const doCloseCreateOrganizationOverlay = useCallback(() => dispatch(closeCreateOrganizationOverlay()), [dispatch]);
  const doCloseCreateClusterOverlay = useCallback(() => dispatch(closeCreateClusterOverlay()), [dispatch]);
  const doCloseUpdateClusterOverlay = useCallback(() => dispatch(closeUpdateClusterOverlay()), [dispatch]);
  const doCloseCreateServiceOverlay = useCallback(() => dispatch(closeCreateServiceOverlay()), [dispatch]);

  const doOpenCreateProjectOverlay = useCallback(() => {
    const params: OpenCreateProjectOverlayParams = {
      organizationId: organization_id,
    };
    dispatch(openCreateProjectOverlay(params));
  }, [dispatch, organization_id]);

  const doOpenCreateOrganizationOverlay = useCallback(() => {
    dispatch(openCreateOrganizationOverlay());
  }, [dispatch]);

  const doOpenCreateClusterOverlay = useCallback(
    ({
      fromBackup,
      serviceType,
      organizationId,
      projectId,
      clusterId,
    }: {
      serviceType: ServiceType;
      fromBackup?: boolean;
      organizationId?: EntityId;
      projectId?: EntityId;
      clusterId?: EntityId;
    }) => {
      const params: CreateClusterParams = {
        serviceType,
        organizationId: organizationId || undefined,
        projectId: projectId || undefined,
        clusterId: clusterId || undefined,
        fromBackup: fromBackup || false,
      };
      dispatch(openCreateClusterOverlay(params));
    },
    [dispatch],
  );

  const doOpenUpdateClusterOverlay = useCallback(
    ({ serviceType }: { serviceType: ServiceType }) => {
      dispatch(openUpdateClusterOverlay({ serviceType }));
    },
    [dispatch],
  );

  const doOpenCreateServiceOverlay = useCallback(() => {
    const params = {
      organizationId: organization_id || undefined,
      projectId: project_id || undefined,
      clusterId: cluster_id || undefined,
    };
    dispatch(openCreateServiceOverlay(params));
  }, [dispatch, organization_id, project_id, cluster_id]);

  return {
    doCloseCreateProjectOverlay,
    doCloseCreateOrganizationOverlay,
    doCloseCreateClusterOverlay,
    doCloseUpdateClusterOverlay,
    doCloseCreateServiceOverlay,

    doOpenCreateProjectOverlay,
    doOpenCreateOrganizationOverlay,
    doOpenCreateClusterOverlay,
    doOpenUpdateClusterOverlay,
    doOpenCreateServiceOverlay,
  };
};
